import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { aiNoteActions, appointmentActions, formActions, noteActions } from 'store';
import { noteStatusEnums } from 'utils';
import { SessionNoteFormBuilder } from '../../../../../pages/forms/sessionNoteFormBuilder';
import { formModelEnums } from '../../../../../pages/forms/constants';
import { findAiSummaryAttribute } from '../../../../../pages/forms/utils/findAiSummaryAttribute';
import { useAiSessionNoteStream } from '../../../../../utils/hooks/useAiSessionNoteStream';
import { sanitizeAiHtml } from '../../../../../utils/sanitizeAiHtml';
import { OrgIntegrations } from 'utils';

export const CreateAppSessionNote = forwardRef(({ info, afterSuccess, noteType, serviceTemplate, appt, width = 816, onCancel }, ref) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const statusRef = useRef(null);
    const ACTION_TYPE = 'ADD_EDIT_APPT_NOTE';
    const noteTypeEnum = noteType === 'OFFICE_NOTES' ? formModelEnums.OFFICENOTES : formModelEnums.CLINICALNOTES;
    const formById = useSelector((state) => state.forms?.form);
    const aiResult = useSelector((state) => state.aiNote?.sessionNote);
    const { aiBridge } = OrgIntegrations();
    const aiTargetPathRef = useRef(null);
    const aiTargetFormIdRef = useRef(null);
    const triggeredRef = useRef(false);
    const lastConvIdRef = useRef(null);
    const lastFormIdRef = useRef(null);
    const formByIdRef = useRef(null);
    const apptId = appt?.id
    formByIdRef.current = formById;

    const onUpdate = useCallback(({ content }) => {
        const targetPath = aiTargetPathRef.current;
        const targetFormId = aiTargetFormIdRef.current;
        const currentFormId = formByIdRef.current?.id || null;
        const ref = formRef.current;
        if (!targetPath || !ref) return;
        if (!targetFormId || targetFormId !== currentFormId) return;
        ref.setFieldByPath?.(targetPath, sanitizeAiHtml(content));
    }, []);
    const stream = useAiSessionNoteStream({ onUpdate });

    const stopStreamRef = useRef(stream.stop);
    stopStreamRef.current = stream.stop;

    /** Opening an existing note for edit counts as viewing it (creator is filtered server-side). */
    useEffect(() => {
        if (info?.id) dispatch(noteActions.viewNote({ ids: [info.id] }));
    }, [info?.id]);

    useEffect(() => {
        dispatch(aiNoteActions.resetAiSessionNote());
        return () => {
            dispatch(aiNoteActions.resetAiSessionNote());
            dispatch(formActions.resetFormById());
            dispatch(formActions.resetFormDefault());
        };
    }, [dispatch]);

    useEffect(() => {
        const currentFormId = formById?.id || null;
        const prevFormId = lastFormIdRef.current;

        if (prevFormId && prevFormId !== currentFormId) {
            stopStreamRef.current?.();
            aiTargetPathRef.current = null;
            aiTargetFormIdRef.current = null;
            triggeredRef.current = false;
            lastConvIdRef.current = null;
            dispatch(aiNoteActions.resetAiSessionNote());
        }
        lastFormIdRef.current = currentFormId;

        // Auto-trigger evaluation
        if (!aiBridge) return;
        if (info?.id) return;
        if (!apptId || !currentFormId) return;
        if (triggeredRef.current) return;

        const match = findAiSummaryAttribute(formById);
        if (!match) return;

        aiTargetPathRef.current = match.path;
        aiTargetFormIdRef.current = currentFormId;
        triggeredRef.current = true;
        dispatch(aiNoteActions.generateAiSessionNote(apptId, currentFormId));
    }, [apptId, dispatch, formById, info?.id, aiBridge]);

    useEffect(() => {
        if (!aiResult?.conversationId) return;
        if (lastConvIdRef.current === aiResult.conversationId) return;
        lastConvIdRef.current = aiResult.conversationId;
        stream.start(aiResult.conversationId);
    }, [aiResult, stream]);

    useEffect(() => {
        if (stream.isDone) dispatch(aiNoteActions.markAiNoteStreamDone());
        else if (stream.isError) dispatch(aiNoteActions.markAiNoteStreamError());
    }, [stream.isDone, stream.isError, dispatch]);

    useImperativeHandle(ref, () => ({
        submit: () => formRef.current?.submit?.(),
        submitDraft: () => formRef.current?.submitDraft?.(),
        isDirty: () => !!formRef.current?.isDirty?.(),
        canAutoSave: () => !!formRef.current?.canAutoSave?.(),
    }));

    const handleSaveNote = (data) => {
        const noteData = {
            ...data,
            resource: apptId,
            type: noteType,
            status: data.status,
        };
        delete noteData?.id;
        if (info) noteData.id = info?.id;
        dispatch(appointmentActions.addEditApptNote(apptId, noteData));
    };

    return (
        <Box sx={{ width }}>
            <SessionNoteFormBuilder
                ref={formRef}
                width={width}
                info={info}
                resourceId={apptId}
                formModel={noteTypeEnum}
                afterSuccess={afterSuccess}
                ACTION_TYPE={ACTION_TYPE}
                handleSaveNote={handleSaveNote}
                serviceTemplate={serviceTemplate}
                statusRef={statusRef}
                appt={appt}
                onCancel={onCancel}
            />
        </Box>
    );
});
